import { Injectable } from '@angular/core';
import { dollarsToCents } from '@core/util/money';
import { FCTCredit, FCTFee } from '@fnModels/fct';
import { FCTFunctions } from '@functions/fct.service';
import { MatterBusinessUser } from '@models/matter';
import {
	FCTDocumentType,
	FCTTitleInsuranceState,
	TitleInsuranceCredit,
	TitleInsuranceCreditType,
	TitleInsuranceFee,
	TitleInsurancePolicy,
	TitleInsurancePolicyType,
	TitleInsuranceQuote,
} from '@models/title-insurance';
import { MatterMortgage } from '@models/mortgage';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterActions, MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { envTypes } from 'src/environments/env.types';
import { environment } from 'src/environments/environment';
import {
	MatterFCTResidentialTitleInsurance,
	FCTSendData,
} from '@models/title-insurance/fct';
import { MatterRepo } from '@repos/matter/matter.repo';
import { firstValueFrom } from 'rxjs';
import { dispatch } from '@ngxs/store';
import { FctHttpService } from '@services/fct/fct-http.service';

const FCT_COUNSEL_FEE_TYPE = 'reviewing counsel fee';

@Injectable({
	providedIn: 'root',
})
export class FctGetStatusService {
	@SelectSnapshot(SelectedMatter.assignedLawyer)
	assignedLawyer: MatterBusinessUser;
	@SelectSnapshot(SelectedMatter.getTitleInsuranceState)
	state: FCTTitleInsuranceState;
	@SelectSnapshot(MatterMortgagesState.getAllMortgages)
	mortgages: MatterMortgage[];
	@SelectSnapshot(SelectedMatter.closingDate) closingDate: string;

	private setTitleInsurance = dispatch(MatterActions.SetTitleInsurance);

	constructor(
		private matterDispatch: MatterDispatch,
		private snackbarService: SnackbarService,
		private fctFunctions: FCTFunctions,
		private matterRepo: MatterRepo,
		private fctHttpService: FctHttpService,
	) {}

	async updateStatus(matterId: string, thirdPartyId: string) {
		try {
			const status = await this.fctFunctions
				.getStatus(this.assignedLawyer?.id, thirdPartyId)
				.pipe(take(1))
				.toPromise();

			if (environment.env !== envTypes.PRODUCTION) {
				console.log('Status from FCT', status);
			}

			LogRocket.log('Status from FCT', status);

			const titleInsurance: Partial<MatterFCTResidentialTitleInsurance> = {
				state: status.status,
				fees: [],
				credits: [],
				documents: [],
				basePremium: null,
				premium: null,
				premiumWithCounselCreditApplied: null,
				orderedOn: null,
				policies: [],
				extendCreditAmount: null,
			};

			if (titleInsurance.state === FCTTitleInsuranceState.CANCELLED) {
				this.matterDispatch.SetTitleInsurance(matterId, titleInsurance);

				return;
			}

			if (status?.quote) {
				// save as quote
				const extendCreditAmount = this.getExtendToClientsCredits(
					status?.quote?.credits,
				);
				const amountInclCredits = dollarsToCents(status?.quote?.amountDue);

				const quote: TitleInsuranceQuote = {
					lastUpdated: status?.quoteTimestamp,
					basePremium: dollarsToCents(status?.quote?.totalAmount),
					premiumWithCounselCreditApplied: amountInclCredits,
					fees: this.buildFees(status?.quote?.fees),
					credits: this.buildCredits(status?.quote?.credits),
					extendCreditAmount,
					premium: amountInclCredits + extendCreditAmount,
				};

				titleInsurance.quote = quote;
			}

			if (status?.feesInfo) {
				titleInsurance.basePremium = dollarsToCents(
					status?.feesInfo?.totalAmount,
				);
				titleInsurance.premiumWithCounselCreditApplied = dollarsToCents(
					status?.feesInfo?.amountDue,
				);

				titleInsurance.policies =
					status?.policies?.map((policy) => {
						let mortgage = null;

						if (policy.mortgageSourceId) {
							const allMortgages = [...this.mortgages];
							mortgage =
								allMortgages?.find(
									(x) => x.numericId === policy.mortgageSourceId,
								) ?? null;
						}

						const _policy: TitleInsurancePolicy = {
							type:
								policy.policyType.toLowerCase() === 'lender'
									? TitleInsurancePolicyType.LENDER
									: TitleInsurancePolicyType.OWNER,
							name:
								policy.policyType.toLowerCase() === 'lender'
									? policy.lenderName
									: policy.ownerName,
							number: policy.policyNumber,
							insuredAmount: dollarsToCents(policy.insuredAmount),
							priority: mortgage?.priority ?? null,
						};

						return _policy;
					}) ?? [];

				titleInsurance.fees = this.buildFees(status?.feesInfo?.fees);
				titleInsurance.credits = this.buildCredits(status?.feesInfo?.credits);
				titleInsurance.extendCreditAmount = this.getExtendToClientsCredits(
					status?.feesInfo?.credits,
				);
				titleInsurance.premium =
					+titleInsurance.premiumWithCounselCreditApplied +
					+titleInsurance.extendCreditAmount;
			}

			titleInsurance.documents = [];

			if (status?.generatedDocuments) {
				titleInsurance.documents =
					status?.generatedDocuments?.map((doc) => ({
						name: doc.name as FCTDocumentType,
						issuedOn: doc.generatedDateTime ?? null,
					})) ?? [];
			}
			titleInsurance.orderedOn = status.orderDate;
			titleInsurance.lastUpdated = new Date().getTime();

			if (environment.env !== envTypes.PRODUCTION) {
				console.log('Mapped FCT to matter title insurance', titleInsurance);
			}

			await firstValueFrom(
				this.setTitleInsurance({ matterId, titleInsurance }),
			);
			// send background job to save documents to Closer. The Cloud Functions and FE logic will eventually
			// be completely ported to C#.
			this.fctHttpService.syncStatus();
		} catch (err) {
			console.log('Error getting fct status of order', err);
			LogRocket.log('Error getting fct order status', err);
			this.snackbarService.openSnackBar({
				text: 'An error occurred when fetching the latest status of this order from FCT',
				type: 'error',
			});
		}
	}

	private buildFees(fees: FCTFee[]): TitleInsuranceFee[] {
		return (
			fees?.map((x) => ({
				description: x.displayName,
				amount: dollarsToCents(x.amount),
				tax: dollarsToCents(x.totalTaxAmount),
				total: dollarsToCents(x.total),
			})) ?? []
		);
	}

	private getExtendToClientsCredits(credits: FCTCredit[]) {
		const counselCredit = credits?.find((x) =>
			this.isCounselFeeType(x.feeType),
		);

		return counselCredit ? Math.abs(dollarsToCents(counselCredit.amount)) : 0;
	}

	private buildCredits(credits: FCTCredit[]): TitleInsuranceCredit[] {
		return (
			credits?.map((x) => {
				const isCounselFee = this.isCounselFeeType(x.feeType);

				return {
					description: isCounselFee ? 'Counsel credit' : x.displayName,
					amount: dollarsToCents(x.amount),
					tax: dollarsToCents(
						x.taxes?.reduce((prev, curr) => +prev + +curr.amount, 0) ?? 0,
					),
					total: dollarsToCents(x.total),
					type: isCounselFee
						? TitleInsuranceCreditType.TITLE_INSURANCE_COUNSEL_FEE
						: TitleInsuranceCreditType.CUSTOMER_SERVICE_CREDIT,
				};
			}) ?? []
		);
	}

	private isCounselFeeType(feeType: string) {
		return feeType.toLowerCase() === FCT_COUNSEL_FEE_TYPE;
	}

	async updateFCTData(
		matterId: string,
		assignLawyerId: string,
		titleInsurance: MatterFCTResidentialTitleInsurance,
		fctData: FCTSendData,
		payload: FCTSendData,
	): Promise<{ matterId: string; url: string }> {
		const result = await this.fctFunctions
			.sendData(assignLawyerId, fctData)
			.pipe(take(1))
			.toPromise();

		LogRocket.log('Result from FCT - send data', result);

		await this.matterRepo.Update(matterId, {
			titleInsurance: {
				...titleInsurance,
				portalUrl: result.url,
				thirdPartyId: result.dealUrn,
				previousSubmittedPayload: { ...payload, dealUrn: result.dealUrn },
			},
		});

		return {
			matterId,
			url: result.url,
		};
	}
}
